import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { ProfileLink } from "@/components/ProfileDrawer";
import { formatDateTimeWAT } from "@/lib/format";
import { toast } from "sonner";
import { Filter, CheckCircle2, MessageSquare } from "lucide-react";

const TYPES = ["", "DAR", "INCIDENT", "PASSDOWN", "HOURLY"];

export const ReportsAdmin = () => {
  const [reports, setReports] = useState([]);
  const [sites, setSites] = useState([]);
  const [guards, setGuards] = useState([]);
  const [type, setType] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    const q = type ? `?type=${type}` : "";
    const r = await api.get(`/reports${q}`);
    setReports(r.data);
  }, [type]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/sites").then(r => setSites(r.data));
    api.get("/guards").then(r => setGuards(r.data));
  }, []);

  const sitesById = useMemo(
    () => Object.fromEntries(sites.map(s => [s.id, s.name])),
    [sites]
  );
  const guardsById = useMemo(
    () => Object.fromEntries(guards.map(g => [g.id, g.full_name])),
    [guards]
  );

  const visibleRows = useMemo(() => reports.slice(0, 100), [reports]);

  const siteName = (id) => sitesById[id] || id;
  const guardName = (id) => guardsById[id] || "—";

  const resolve = async (id) => {
    await api.put(`/reports/${id}/resolve`);
    toast.success("Report marked resolved");
    load();
  };

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 s-card" style={{ padding: 0 }}>
        <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: "var(--border-dark)" }}>
          <div className="h-section">Reports feed</div>
          <div className="flex items-center gap-2">
            <Filter size={14} color="#8CA0B8" />
            <select value={type} onChange={(e) => setType(e.target.value)} className="s-input" style={{ width: 140 }} data-testid="filter-type">
              <option value="">All types</option>
              {TYPES.filter(Boolean).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <table className="s-table">
          <thead><tr><th>Submitted</th><th>Type</th><th>Site</th><th>Guard</th><th>Status</th></tr></thead>
          <tbody>
            {visibleRows.map(r => (
              <tr key={r.id} onClick={() => setSelected(r)} style={{ cursor: "pointer" }} data-testid={`report-row-${r.id}`}>
                <td className="mono text-muted" style={{ fontSize: 12 }}>{formatDateTimeWAT(r.created_at)}</td>
                <td><StatusBadge status={r.type} /></td>
                <td>{siteName(r.site_id)}</td>
                <td className="text-muted">{r.guard_id ? <ProfileLink kind="guard" id={r.guard_id}>{guardName(r.guard_id)}</ProfileLink> : guardName(r.guard_id)}</td>
                <td>{r.type === "INCIDENT" && !r.resolved ? <StatusBadge status="INCIDENT" label="Open" /> :
                     <StatusBadge status="RESOLVED" label={r.type === "INCIDENT" ? "Resolved" : "Logged"} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="s-card">
        {selected ? (
          <>
            <div className="flex justify-between items-start mb-2">
              <StatusBadge status={selected.type} />
              <span className="text-muted mono" style={{ fontSize: 11 }}>{formatDateTimeWAT(selected.created_at)}</span>
            </div>
            <div className="h-section mt-2">{siteName(selected.site_id)}</div>
            <div className="text-muted" style={{ fontSize: 12, marginBottom: 12 }}>By {guardName(selected.guard_id)}</div>
            <div style={{ fontSize: 14, lineHeight: 1.6, color: "#fff", whiteSpace: "pre-wrap" }}>
              {selected.text}
            </div>
            {selected.incident_category && (
              <div className="mt-3"><StatusBadge status="INCIDENT" label={`Category · ${selected.incident_category}`} /></div>
            )}
            {selected.lat && (
              <div className="text-muted mono mt-3" style={{ fontSize: 11 }}>
                {selected.lat?.toFixed(4)}, {selected.lng?.toFixed(4)}
              </div>
            )}
            {selected.type === "INCIDENT" && !selected.resolved && (
              <button onClick={() => resolve(selected.id)} className="btn-primary mt-4 w-full inline-flex items-center justify-center gap-2" data-testid="resolve-report">
                <CheckCircle2 size={14} /> Mark resolved
              </button>
            )}
          </>
        ) : (
          <div className="text-muted" style={{ fontSize: 13, padding: 8 }}>
            <MessageSquare size={16} className="mb-2" />
            Select a report from the feed.
          </div>
        )}
      </div>
    </div>
  );
};
